import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Grid,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Chip,
  Divider,
} from "@mui/material";
import {
  ContentCopy as CopyIcon,
  ArrowBack as BackIcon,
  Description as MemoIcon,
  PictureAsPdf as PdfIcon,
  Description as DocxIcon,
  Code as MdIcon,
  FileDownload as DownloadIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import ReactMarkdown from "react-markdown";
import { tokens } from "@/tokens";
import { useSaveFirstSaleGeneratedDocMutation } from "@/services/first-sale/first-sale-api";
import {
  generateMemoFromTemplate,
  type MemoTemplateInputs,
} from "@/utils/firstSaleMemoTemplate";
import {
  downloadMarkdownAsPdf,
  downloadMarkdownAsDocx,
  downloadMarkdownAsFile,
} from "@/utils/markdownExport";

interface GenerateMemoModalProps {
  open: boolean;
  onClose: () => void;
  caseData: any;
  supplier: any;
  documents: any[];
  validation: any;
}

const GenerateMemoModal: React.FC<GenerateMemoModalProps> = ({
  open,
  onClose,
  caseData,
  supplier,
}) => {
  const [form, setForm] = useState<MemoTemplateInputs>({
    importerName: "",
    middlemanName: "",
    factoryName: "",
    intermediaryName: "",
    productDescription: "",
    countryOfOrigin: "China",
    htsCode: "",
    dutyRate: undefined,
    middlemanUnitPrice: undefined,
    factoryUnitPrice: undefined,
    intermediaryUnitPrice: undefined,
    attorney: "",
    caseNumber: "",
    poNumber: "",
  });

  const [memoContent, setMemoContent] = useState("");
  const [copied, setCopied] = useState(false);
  const [downloadAnchor, setDownloadAnchor] = useState<null | HTMLElement>(null);
  const [exporting, setExporting] = useState(false);
  const [saved, setSaved] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [includeMiddlemanValue, setIncludeMiddlemanValue] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [includeFactoryValue, setIncludeFactoryValue] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [includeSavings, setIncludeSavings] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [includeLinkedPOs, setIncludeLinkedPOs] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [includeHtsCode, setIncludeHtsCode] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [includeDutyRate, setIncludeDutyRate] = useState(true);

  const [saveDoc, { isLoading: saving }] = useSaveFirstSaleGeneratedDocMutation();

  // Pre-fill from case data when modal opens
  useEffect(() => {
    if (open && caseData) {
      const middlemanVal = caseData?.middlemanValue;
      const factoryVal = caseData?.factoryValue;
      const savingsVal = caseData?.savings;

      // Compute unit prices from total values if quantity is in the product description
      const qtyMatch = caseData?.productDescription?.match(/([\d,]+)\s*units/i);
      const qty = qtyMatch ? parseInt(qtyMatch[1].replace(/,/g, ""), 10) : 0;

      setForm((prev) => ({
        ...prev,
        middlemanName: supplier?.supplierName || caseData?.middlemanName || prev.middlemanName,
        factoryName: caseData?.factoryName || prev.factoryName,
        productDescription: caseData?.productDescription || prev.productDescription,
        htsCode: caseData?.htsCode || prev.htsCode,
        caseNumber: caseData?.caseNumber || prev.caseNumber,
        poNumber: caseData?.poNumber || prev.poNumber,
        countryOfOrigin: supplier?.factoryCountry || supplier?.country || prev.countryOfOrigin || "China",
        middlemanUnitPrice: qty > 0 && middlemanVal ? parseFloat((middlemanVal / qty).toFixed(2)) : prev.middlemanUnitPrice,
        factoryUnitPrice: qty > 0 && factoryVal ? parseFloat((factoryVal / qty).toFixed(2)) : prev.factoryUnitPrice,
        dutyRate: caseData?.dutyRate ?? prev.dutyRate,
      }));

      // Smart defaults: include values that exist, exclude what's missing
      setIncludeMiddlemanValue(middlemanVal != null && middlemanVal > 0);
      setIncludeFactoryValue(factoryVal != null && factoryVal > 0);
      setIncludeSavings(savingsVal != null && savingsVal > 0);
      setIncludeLinkedPOs(caseData?.linkedPOs?.length > 0);
      setIncludeHtsCode(!!caseData?.htsCode);
      setIncludeDutyRate(caseData?.dutyRate != null && caseData.dutyRate > 0);
    }
  }, [open, caseData, supplier]);

  const updateField = (field: keyof MemoTemplateInputs) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const val = e.target.value;
    setForm((prev) => ({
      ...prev,
      [field]:
        ["dutyRate", "middlemanUnitPrice", "factoryUnitPrice", "intermediaryUnitPrice"].includes(field)
          ? val === "" ? undefined : parseFloat(val)
          : val,
    }));
  };

  const canGenerate = form.importerName.trim() && form.middlemanName.trim() && form.factoryName.trim();

  const handleGenerate = () => {
    setMemoContent(generateMemoFromTemplate(form));
    setSaved(false);
  };

  const handleSave = async () => {
    if (!caseData?._id) return;
    await saveDoc({
      caseId: caseData._id,
      type: "memo",
      title: `First Sale Memo — ${form.caseNumber || caseData.caseNumber || "Draft"}`,
      content: memoContent,
    }).unwrap();
    setSaved(true);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(memoContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const baseFilename = `${form.caseNumber || caseData?.caseNumber || "first-sale"}-memo`;

  const handleClose = () => {
    setMemoContent("");
    setCopied(false);
    setSaved(false);
    onClose();
  };

  const generated = memoContent.length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={generated ? "lg" : "md"}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: tokens.radius.lg,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          maxHeight: "90vh",
          minHeight: generated ? "80vh" : undefined,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: tokens.fontSize.lg,
          fontWeight: tokens.fontWeight.heavy,
          color: tokens.text.primary,
          pb: 0.5,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          {generated && (
            <IconButton size="small" onClick={() => setMemoContent("")} sx={{ mr: 0.5 }}>
              <BackIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
          <MemoIcon sx={{ fontSize: 20, color: tokens.brand.primary }} />
          <span>{generated ? "First Sale Memo" : "Generate First Sale Memo"}</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {!generated ? (
          <Stack spacing={2.5} mt={1}>
            {/* Parties */}
            <Box>
              <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 1 }}>
                Parties
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <TextField label="Importer Name *" value={form.importerName} onChange={updateField("importerName")} fullWidth size="small" placeholder="e.g., Brickcraft LLC" />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Middleman Name *" value={form.middlemanName} onChange={updateField("middlemanName")} fullWidth size="small" placeholder="e.g., JK Enterprises" />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Factory Name *" value={form.factoryName} onChange={updateField("factoryName")} fullWidth size="small" placeholder="e.g., Gold's Manufacturing" />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Intermediary (optional)" value={form.intermediaryName || ""} onChange={updateField("intermediaryName")} fullWidth size="small" placeholder="e.g., DeCool Trading" />
                </Grid>
              </Grid>
            </Box>

            {/* Product */}
            <Box>
              <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 1 }}>
                Product
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <TextField label="Product Description" value={form.productDescription} onChange={updateField("productDescription")} fullWidth size="small" placeholder="e.g., Cotton terry towels" />
                </Grid>
                <Grid item xs={3}>
                  <TextField label="Country of Origin" value={form.countryOfOrigin} onChange={updateField("countryOfOrigin")} fullWidth size="small" />
                </Grid>
                <Grid item xs={3}>
                  <TextField label="HTS Code" value={form.htsCode || ""} onChange={updateField("htsCode")} fullWidth size="small" placeholder="e.g., 6302.60" />
                </Grid>
              </Grid>
            </Box>

            {/* Case Values — include/exclude with checkboxes */}
            {caseData && (caseData.middlemanValue || caseData.factoryValue || caseData.linkedPOs?.length > 0) && (
              <Box>
                <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 0.5 }}>
                  Case Values
                </Typography>
                <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, mb: 1.5 }}>
                  Toggle which values to include in the memorandum
                </Typography>
                <Box
                  sx={{
                    border: `1px solid ${tokens.border.container}`,
                    borderRadius: tokens.radius.md,
                    bgcolor: tokens.bg.muted,
                    p: 1.5,
                  }}
                >
                  <Stack spacing={0.5}>
                    {caseData.middlemanValue != null && (
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <FormControlLabel
                          control={<Checkbox checked={includeMiddlemanValue} onChange={(e) => setIncludeMiddlemanValue(e.target.checked)} size="small" sx={{ py: 0.25, color: tokens.text.disabled, "&.Mui-checked": { color: tokens.brand.primary } }} />}
                          label={<Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.primary }}>Middleman Order Value</Typography>}
                          sx={{ mx: 0 }}
                        />
                        <Chip label={new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(caseData.middlemanValue)} size="small" sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.bold, fontVariantNumeric: "tabular-nums", height: 24, bgcolor: includeMiddlemanValue ? `${tokens.brand.primary}10` : tokens.bg.hover, color: includeMiddlemanValue ? tokens.brand.primary : tokens.text.muted, borderRadius: tokens.radius.sm }} />
                      </Stack>
                    )}
                    {caseData.factoryValue != null && (
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <FormControlLabel
                          control={<Checkbox checked={includeFactoryValue} onChange={(e) => setIncludeFactoryValue(e.target.checked)} size="small" sx={{ py: 0.25, color: tokens.text.disabled, "&.Mui-checked": { color: tokens.brand.primary } }} />}
                          label={<Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.primary }}>Factory Order Value</Typography>}
                          sx={{ mx: 0 }}
                        />
                        <Chip label={new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(caseData.factoryValue)} size="small" sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.bold, fontVariantNumeric: "tabular-nums", height: 24, bgcolor: includeFactoryValue ? `${tokens.status.success}10` : tokens.bg.hover, color: includeFactoryValue ? tokens.status.success : tokens.text.muted, borderRadius: tokens.radius.sm }} />
                      </Stack>
                    )}
                    {caseData.savings != null && caseData.savings > 0 && (
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <FormControlLabel
                          control={<Checkbox checked={includeSavings} onChange={(e) => setIncludeSavings(e.target.checked)} size="small" sx={{ py: 0.25, color: tokens.text.disabled, "&.Mui-checked": { color: tokens.brand.primary } }} />}
                          label={<Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.primary }}>Estimated Duty Savings</Typography>}
                          sx={{ mx: 0 }}
                        />
                        <Chip label={new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(caseData.savings)} size="small" sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.bold, fontVariantNumeric: "tabular-nums", height: 24, bgcolor: includeSavings ? `${tokens.status.success}10` : tokens.bg.hover, color: includeSavings ? tokens.status.success : tokens.text.muted, borderRadius: tokens.radius.sm }} />
                      </Stack>
                    )}
                    {caseData.htsCode && (
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <FormControlLabel
                          control={<Checkbox checked={includeHtsCode} onChange={(e) => setIncludeHtsCode(e.target.checked)} size="small" sx={{ py: 0.25, color: tokens.text.disabled, "&.Mui-checked": { color: tokens.brand.primary } }} />}
                          label={<Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.primary }}>HTS Classification</Typography>}
                          sx={{ mx: 0 }}
                        />
                        <Chip label={caseData.htsCode} size="small" sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.semibold, fontFamily: "monospace", height: 24, bgcolor: includeHtsCode ? `${tokens.brand.primary}10` : tokens.bg.hover, color: includeHtsCode ? tokens.brand.primary : tokens.text.muted, borderRadius: tokens.radius.sm }} />
                      </Stack>
                    )}
                    {caseData.dutyRate != null && (
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <FormControlLabel
                          control={<Checkbox checked={includeDutyRate} onChange={(e) => setIncludeDutyRate(e.target.checked)} size="small" sx={{ py: 0.25, color: tokens.text.disabled, "&.Mui-checked": { color: tokens.brand.primary } }} />}
                          label={<Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.primary }}>Duty Rate</Typography>}
                          sx={{ mx: 0 }}
                        />
                        <Chip label={`${caseData.dutyRate}%`} size="small" sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.bold, fontVariantNumeric: "tabular-nums", height: 24, bgcolor: includeDutyRate ? `${tokens.status.warning}10` : tokens.bg.hover, color: includeDutyRate ? tokens.status.warning : tokens.text.muted, borderRadius: tokens.radius.sm }} />
                      </Stack>
                    )}
                    {caseData.linkedPOs?.length > 0 && (
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <FormControlLabel
                          control={<Checkbox checked={includeLinkedPOs} onChange={(e) => setIncludeLinkedPOs(e.target.checked)} size="small" sx={{ py: 0.25, color: tokens.text.disabled, "&.Mui-checked": { color: tokens.brand.primary } }} />}
                          label={<Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.primary }}>Linked Purchase Orders ({caseData.linkedPOs.length})</Typography>}
                          sx={{ mx: 0 }}
                        />
                        <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, fontFamily: "monospace" }}>
                          {caseData.linkedPOs.slice(0, 3).join(", ")}{caseData.linkedPOs.length > 3 ? `, +${caseData.linkedPOs.length - 3}` : ""}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Box>
              </Box>
            )}

            <Divider sx={{ borderColor: tokens.border.light }} />

            {/* Pricing */}
            <Box>
              <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 0.5 }}>
                Unit Pricing
              </Typography>
              <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, mb: 1.5 }}>
                {form.middlemanUnitPrice || form.factoryUnitPrice ? "Pre-calculated from case values" : "Enter per-unit prices to show savings example"}
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={3}>
                  <TextField
                    label="Importer Price / unit"
                    value={form.middlemanUnitPrice ?? ""}
                    onChange={updateField("middlemanUnitPrice")}
                    fullWidth size="small" type="number"
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  />
                </Grid>
                {form.intermediaryName?.trim() && (
                  <Grid item xs={3}>
                    <TextField
                      label="Intermediary Price / unit"
                      value={form.intermediaryUnitPrice ?? ""}
                      onChange={updateField("intermediaryUnitPrice")}
                      fullWidth size="small" type="number"
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    />
                  </Grid>
                )}
                <Grid item xs={3}>
                  <TextField
                    label="Factory Price / unit"
                    value={form.factoryUnitPrice ?? ""}
                    onChange={updateField("factoryUnitPrice")}
                    fullWidth size="small" type="number"
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    label="Duty Rate"
                    value={form.dutyRate ?? ""}
                    onChange={updateField("dutyRate")}
                    fullWidth size="small" type="number"
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Meta */}
            <Box>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <TextField label="Prepared By (optional)" value={form.attorney || ""} onChange={updateField("attorney")} fullWidth size="small" />
                </Grid>
                <Grid item xs={3}>
                  <TextField label="Case #" value={form.caseNumber || ""} onChange={updateField("caseNumber")} fullWidth size="small" />
                </Grid>
                <Grid item xs={3}>
                  <TextField label="PO #" value={form.poNumber || ""} onChange={updateField("poNumber")} fullWidth size="small" />
                </Grid>
              </Grid>
            </Box>
          </Stack>
        ) : (
          <Box
            sx={{
              mt: 1,
              border: `1px solid ${tokens.border.container}`,
              borderRadius: tokens.radius.md,
              bgcolor: tokens.bg.surface,
              p: 2.5,
              maxHeight: "70vh",
              overflow: "auto",
              fontSize: tokens.fontSize.sm2,
              lineHeight: 1.8,
              color: tokens.text.body,
              "& h1": { fontSize: tokens.fontSize.lg, fontWeight: tokens.fontWeight.heavy, mt: 2, mb: 1 },
              "& h2": { fontSize: tokens.fontSize.md, fontWeight: tokens.fontWeight.bold, mt: 2, mb: 0.5 },
              "& h3": { fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.semibold, mt: 1.5, mb: 0.5 },
              "& ul, & ol": { pl: 3, my: 0.5 },
              "& li": { mb: 0.25 },
              "& p": { mb: 1 },
              "& strong": { fontWeight: tokens.fontWeight.bold },
              "& table": { borderCollapse: "collapse", width: "100%", my: 1 },
              "& th, & td": { border: `1px solid ${tokens.border.default}`, px: 1.5, py: 0.75, fontSize: tokens.fontSize.xs, textAlign: "left" },
              "& th": { bgcolor: tokens.bg.hover, fontWeight: tokens.fontWeight.semibold },
            }}
          >
            <ReactMarkdown>{memoContent}</ReactMarkdown>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {!generated ? (
          <>
            <Button onClick={handleClose} sx={{ textTransform: "none", fontSize: tokens.fontSize.sm2, color: tokens.text.secondary }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={!canGenerate}
              startIcon={<MemoIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: "none", fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.semibold,
                bgcolor: tokens.brand.primary, borderRadius: tokens.radius.md, boxShadow: "none", px: 3,
                "&:hover": { bgcolor: tokens.brand.primaryHover, boxShadow: "none" },
              }}
            >
              Generate Memo
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose} sx={{ textTransform: "none", fontSize: tokens.fontSize.sm2, color: tokens.text.secondary }}>
              Close
            </Button>
            {caseData?._id && (
              <Button
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
                onClick={handleSave}
                disabled={saving || saved}
                variant="outlined"
                sx={{
                  textTransform: "none", fontSize: tokens.fontSize.sm2,
                  color: saved ? tokens.status.success : tokens.text.primary,
                  borderColor: saved ? tokens.status.success : tokens.border.default,
                  borderRadius: tokens.radius.md,
                }}
              >
                {saved ? "Saved" : "Save to Case"}
              </Button>
            )}
            <Button
              startIcon={<CopyIcon sx={{ fontSize: 16 }} />}
              onClick={handleCopy}
              variant="outlined"
              sx={{
                textTransform: "none", fontSize: tokens.fontSize.sm2, color: tokens.text.primary,
                borderColor: tokens.border.default, borderRadius: tokens.radius.md,
              }}
            >
              {copied ? "Copied" : "Copy"}
            </Button>
            <Button
              startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon sx={{ fontSize: 16 }} />}
              variant="contained"
              disabled={exporting}
              onClick={(e) => setDownloadAnchor(e.currentTarget)}
              sx={{
                textTransform: "none", fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.semibold,
                bgcolor: tokens.brand.primary, borderRadius: tokens.radius.md, boxShadow: "none", px: 3,
                "&:hover": { bgcolor: tokens.brand.primaryHover, boxShadow: "none" },
              }}
            >
              Download
            </Button>
            <Menu
              anchorEl={downloadAnchor}
              open={!!downloadAnchor}
              onClose={() => setDownloadAnchor(null)}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "bottom", horizontal: "right" }}
              slotProps={{ paper: { sx: { borderRadius: tokens.radius.md, boxShadow: "0 4px 20px rgba(0,0,0,0.12)", minWidth: 180 } } }}
            >
              <MenuItem onClick={async () => { setDownloadAnchor(null); setExporting(true); try { await downloadMarkdownAsPdf(memoContent, baseFilename); } finally { setExporting(false); } }} sx={{ fontSize: tokens.fontSize.sm2 }}>
                <ListItemIcon><PdfIcon sx={{ fontSize: 18, color: "#e53935" }} /></ListItemIcon>
                <ListItemText>PDF</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { setDownloadAnchor(null); downloadMarkdownAsDocx(memoContent, baseFilename); }} sx={{ fontSize: tokens.fontSize.sm2 }}>
                <ListItemIcon><DocxIcon sx={{ fontSize: 18, color: "#1565c0" }} /></ListItemIcon>
                <ListItemText>Word (.doc)</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { setDownloadAnchor(null); downloadMarkdownAsFile(memoContent, baseFilename); }} sx={{ fontSize: tokens.fontSize.sm2 }}>
                <ListItemIcon><MdIcon sx={{ fontSize: 18, color: "#6B7280" }} /></ListItemIcon>
                <ListItemText>Markdown (.md)</ListItemText>
              </MenuItem>
            </Menu>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GenerateMemoModal;
